import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

// Active Contracts table styles, scoped to Financials and Payroll (issue #1891).
export const operationalColors = {
  header: '#f3e5c1',
  heading: '#2b2418',
  text: '#473f30',
  divider: '#f0e8db',
  hover: '#fffdf6',
  group: '#0d1016',
  surface: '#fffdf9',
  muted: '#938772',
  controlBorder: 'rgba(192, 176, 134, 0.6)',
  danger: '#ea587d',
  dangerBackground: '#fff4f7',
  dangerBorder: '#f5d8e1'
};

export const operationalSurfaceSx = {
  borderRadius: '28px',
  border: '1px solid rgba(215, 196, 152, 0.42)',
  background: 'linear-gradient(180deg, #fffdf9 0%, #fff9f0 18%, #fffdfa 100%)',
  boxShadow: '0 22px 45px rgba(31, 24, 10, 0.10)',
  overflow: 'hidden',
  mx: { xs: 1.5, md: 2.5 },
  my: { xs: 1.5, md: 2.5 }
};

export const operationalHeaderSx = {
  backgroundColor: operationalColors.header,
  color: operationalColors.heading,
  borderBottom: 'none',
  fontSize: '0.68rem',
  fontWeight: 800,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  whiteSpace: 'nowrap' as const,
  py: 1.55,
  px: 1.6,
  boxShadow: 'inset 0 -1px 0 rgba(123, 104, 64, 0.12)'
};

const softStatus = (
  color: string,
  backgroundColor: string,
  borderColor: string
) => ({
  color,
  backgroundColor,
  border: `1px solid ${borderColor}`,
  '& .MuiChip-icon': { color: 'inherit' }
});

export const operationalToolbarSx: SystemStyleObject<Theme> = {
  px: { xs: 2, md: 3 },
  pt: { xs: 2, md: 2.25 },
  pb: 1.5,
  gap: 1.5,
  flexWrap: 'wrap',
  color: '#514936',
  '& .MuiOutlinedInput-root': {
    minHeight: 40,
    borderRadius: '999px',
    backgroundColor: '#fff',
    color: '#1e1a13',
    fontSize: '13px',
    fontWeight: 700,
    boxShadow: '0 6px 16px rgba(23, 19, 11, 0.06)',
    '& .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${operationalColors.controlBorder}`
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(192, 176, 134, 0.9)'
    }
  },
  '& .MuiButton-root': { borderRadius: '999px', textTransform: 'none' },
  '& .MuiButton-contained': {
    backgroundColor: 'rgba(247, 164, 8, 0.18)',
    color: '#17130b',
    boxShadow: 'none',
    '&:hover': { backgroundColor: operationalColors.header }
  },
  '& .MuiButton-outlined': {
    borderColor: operationalColors.controlBorder,
    color: '#6b604b'
  },
  '& :focus-visible': {
    outline: `2px solid ${operationalColors.heading}`,
    outlineOffset: 2
  }
};

export const operationalTableSx: SystemStyleObject<Theme> = {
  px: { xs: 1.5, md: 2.5 },
  backgroundColor: 'transparent',
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${operationalColors.divider}`
  },
  '& thead .MuiTableCell-root': operationalHeaderSx,
  '& thead .MuiTableCell-root:first-of-type': { borderTopLeftRadius: '16px' },
  '& thead .MuiTableCell-root:last-of-type': { borderTopRightRadius: '16px' },
  '& .MuiTableSortLabel-root, & .MuiTableSortLabel-icon': { color: 'inherit' },
  '& .MuiTableRow-hover': {
    backgroundColor: operationalColors.surface,
    '& > td': {
      color: operationalColors.text,
      py: 1.25,
      px: 1.6,
      fontSize: '0.84rem'
    },
    '&:hover, &:hover > td': { backgroundColor: operationalColors.hover }
  },
  '& .operational-group > td': {
    py: 1.4,
    borderBottom: 'none',
    backgroundColor: 'inherit',
    color: 'inherit'
  },
  '& .operational-group .MuiBox-root, & .operational-group .MuiTypography-root, & .operational-group .MuiIconButton-root':
    { color: 'inherit' },
  '& .operational-group > td:first-of-type': { borderRadius: '14px 0 0 14px' },
  '& .operational-group > td:last-of-type': { borderRadius: '0 14px 14px 0' },
  '& .MuiChip-root': {
    height: 28,
    borderRadius: '999px',
    fontSize: '0.74rem',
    fontWeight: 600,
    letterSpacing: '0.01em'
  },
  '& .MuiChip-colorSuccess, & .MuiIconButton-colorSuccess': softStatus(
    '#72af2f',
    '#f7fcf1',
    '#deedca'
  ),
  '& .MuiChip-colorError, & .MuiIconButton-colorError': softStatus(
    '#ea587d',
    '#fff4f7',
    '#f5d8e1'
  ),
  '& .MuiChip-colorWarning, & .MuiIconButton-colorWarning': softStatus(
    '#d79b28',
    '#fff8ea',
    '#f3e0b7'
  ),
  '& .MuiChip-colorInfo, & .MuiIconButton-colorInfo, & .MuiIconButton-colorPrimary': softStatus(
    '#6f7b8e',
    '#f4f7fb',
    '#dde5ef'
  ),
  '& .MuiIconButton-colorSecondary': softStatus(
    '#7c76df',
    '#f5f3ff',
    '#e5defa'
  ),
  '& .MuiTableRow-hover .MuiIconButton-root': {
    borderRadius: '5px',
    mx: 0.35,
    my: 0.35
  },
  '&.operational-financials .MuiTableRow-hover .MuiIconButton-root': { width: 18, height: 18, p: 0, '& svg': { fontSize: 12 } },
  '& a.bg-black': {
    backgroundColor: '#111318',
    borderRadius: '7px',
    fontSize: '12px',
    fontWeight: 600,
    '&:hover': { backgroundColor: '#1b2028' }
  },
  '& tfoot .MuiTableCell-root': {
    backgroundColor: operationalColors.header,
    color: operationalColors.heading
  },
  '& input, & select': { color: operationalColors.text },
  '& :focus-visible': {
    outline: `2px solid ${operationalColors.heading}`,
    outlineOffset: 2
  },
  '& .operational-group :focus-visible': { outlineColor: 'currentColor' }
};
